import { ref, onMounted } from 'vue'

// Get site key from environment variable or window object
const getSiteKey = () => {
  if (import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
    return import.meta.env.VITE_RECAPTCHA_SITE_KEY
  }
  if (typeof window !== 'undefined' && window.recaptchaSiteKey) {
    return window.recaptchaSiteKey
  }
  // Google's test key (always passes) - replace with your actual site key
  return '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
}

let recaptchaLoaded = false

export const useRecaptcha = () => {
  const isReady = ref(false)
  const error = ref(null)

  const loadRecaptcha = () => {
    return new Promise((resolve, reject) => {
      if (recaptchaLoaded && typeof window.grecaptcha !== 'undefined') {
        isReady.value = true
        resolve()
        return
      }

      if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.ready) {
        recaptchaLoaded = true
        isReady.value = true
        resolve()
        return
      }

      // Wait for Google reCAPTCHA to load
      let attempts = 0
      const maxAttempts = 100 // 10 seconds max
      
      const checkInterval = setInterval(() => {
        attempts++
        if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.ready) {
          clearInterval(checkInterval)
          recaptchaLoaded = true
          isReady.value = true
          resolve()
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval)
          const err = new Error('reCAPTCHA failed to load. Please refresh the page.')
          error.value = err
          reject(err)
        }
      }, 100)
    })
  }

  const executeRecaptcha = async (action = 'submit') => {
    try {
      await loadRecaptcha()

      if (typeof window.grecaptcha === 'undefined') {
        console.warn('reCAPTCHA not loaded')
        return null
      }

      const siteKey = getSiteKey()

      // Use reCAPTCHA v3 (invisible) - this runs in the background
      const token = await new Promise((resolve, reject) => {
        if (window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(siteKey, { action })
              .then((token) => {
                console.log('reCAPTCHA token generated successfully')
                resolve(token)
              })
              .catch((err) => {
                console.error('reCAPTCHA execute error:', err)
                reject(err)
              })
          })
        } else {
          // Fallback: try direct execution
          window.grecaptcha
            .execute(siteKey, { action })
            .then((token) => {
              console.log('reCAPTCHA token generated successfully')
              resolve(token)
            })
            .catch((err) => {
              console.error('reCAPTCHA execute error:', err)
              reject(err)
            })
        }
      })

      return token
    } catch (err) {
      console.error('reCAPTCHA error:', err)
      error.value = err
      // Don't block form submission if reCAPTCHA fails, but log it
      // In production, you might want to block submission
      return null
    }
  }

  return {
    isReady,
    error,
    executeRecaptcha,
    loadRecaptcha
  }
}

